import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, FlaskConical, Globe2, CheckCircle2, Leaf, Lock, Truck, ScanLine, TrendingUp, AlertCircle, MapPin } from "lucide-react";
import AfricaMap from "@/components/AfricaMap";
import WorldMap from "@/components/WorldMap";
import SectionReveal from "@/components/SectionReveal";

const IMG = {
  indoor: "/Images/cultivation-canopy.jpg",
};

const PILLARS = [
  { icon: ShieldCheck, label: "Compliance First", body: "Designed around GACP cultivation, GMP-aligned post-harvest workflows, and destination-market regulatory expectations from day one." },
  { icon: FlaskConical, label: "Pharmaceutical Grade", body: "Controlled-environment indoor cultivation and post-harvest processing engineered for medicinal cannabis specifications." },
  { icon: Globe2, label: "Export-Led", body: "Built primarily for regulated international markets, with domestic pathways pursued only where permitted under applicable Ugandan product regulations." },
  { icon: CheckCircle2, label: "Quality Assured", body: "End-to-end traceability with batch-level analytical verification, documentation, and release controls." },
];

const INFRA = [
  { icon: Leaf, label: "Controlled-Environment Cultivation", body: "Modular indoor cultivation rooms engineered for medicinal cannabis production, environmental control, biosecurity, and repeatable batch quality." },
  { icon: FlaskConical, label: "GMP-Aligned Post-Harvest & Processing", body: "Drying, trimming, curing, packaging, QA release, and documentation workflows designed for regulated export markets, with extraction planned as a future phase." },
  { icon: ScanLine, label: "Traceability Systems", body: "Seed-to-shipment digital traceability across every batch." },
  { icon: Truck, label: "Export Compliance", body: "Licensing, documentation, testing, and logistics calibrated to destination-market requirements." },
  { icon: Lock, label: "Security Infrastructure", body: "Tiered physical and digital security designed to meet controlled-substance expectations." },
];

const OPP = [
  { icon: TrendingUp, label: "Growing demand", body: "Expanding patient access and prescription channels across regulated markets." },
  { icon: AlertCircle, label: "Limited compliant supply", body: "Importers require validated processes, documentation, and consistent batch quality." },
  { icon: MapPin, label: "Uganda advantage", body: "Competitive power, skilled agricultural capability, and export-oriented positioning." },
];

const MARKETS = ["Germany", "United Kingdom", "European Union", "Australia", "Israel", "Canada"];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative pt-[72px] overflow-hidden bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <SectionReveal><div className="overline">East Africa · Regulated · Export-led</div></SectionReveal>
            <SectionReveal delay={120}>
              <h1 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-editorial text-ink">
                Building a regulated <em className="not-italic text-forest">medicinal cannabis</em> export platform from East Africa.
              </h1>
            </SectionReveal>
            <SectionReveal delay={240}>
              <p className="mt-8 max-w-2xl text-ink2 text-base md:text-lg leading-relaxed">
                Maisha Pharma holds a secured commercial license and is executing a $9.5M Series A capital raise to construct a shovel-ready pharmaceutical export platform. At the heart of our Phase I build is a 1,250 m² EU-GMP-aligned post-harvest facility — the "Clean Core" — engineered to support 2,000 m² of indoor flowering today and already sized to scale to 4,000 m² in Phase II.
              </p>
            </SectionReveal>
            <SectionReveal delay={360}>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/our-model" data-testid="hero-cta-model" className="group inline-flex items-center justify-center gap-2 bg-forest text-bone px-7 py-4 text-sm tracking-wide hover:bg-forest/85 transition-colors">
                  Explore Our Model <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link to="/contact" data-testid="hero-cta-inquiries" className="group inline-flex items-center justify-center gap-2 border border-ink/15 text-ink px-7 py-4 text-sm tracking-wide hover:border-forest hover:text-forest transition-colors">
                  Investor & Partnership Inquiries <ArrowUpRight size={16} />
                </Link>
              </div>
            </SectionReveal>
            <SectionReveal delay={480}>
              <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg">
                {[["Compliance","GACP / GMP-aligned"],["Mandate","Export-led"],["Origin","Uganda · East Africa"]].map(([k,v])=>(
                  <div key={k} className="border-t border-line pt-4">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-forest font-semibold">{k}</div>
                    <div className="mt-2 text-sm text-ink">{v}</div>
                  </div>
                ))}
              </div>
            </SectionReveal>
          </div>
          <div className="lg:col-span-5 relative">
            <SectionReveal delay={300}>
              <div className="relative bg-sand/60 p-6 md:p-10 border border-line/60">
                <div className="absolute top-4 right-4 text-[10px] tracking-[0.22em] uppercase text-ink2">FIG.01 · Export Corridor</div>
                <AfricaMap />
                <div className="mt-6 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-ink2">
                  <span>Origin · Uganda</span><span className="text-copper">→</span><span>Regulated Europe</span>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
        <SectionReveal>
          <div className="relative h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="/Images/investors-hero-facility.jpg"
              alt="Pharmaceutical-grade facility infrastructure"
              className="absolute inset-0 w-full h-full object-cover scale-[1.02] transition-transform duration-[1800ms] ease-out hover:scale-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/65 via-forest/10 to-forest/20" />
            <div className="absolute inset-x-0 bottom-0">
              <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8 md:pb-10 flex items-end justify-between gap-4">
                <div className="text-bone/90 text-[10px] md:text-[11px] tracking-[0.22em] uppercase">FIG.05 · Pharmaceutical-grade facility infrastructure</div>
                <div className="hidden md:block h-px flex-1 mx-8 bg-bone/25" />
                <div className="hidden md:block text-bone/70 text-[10px] tracking-[0.22em] uppercase">Uganda · East Africa</div>
              </div>
            </div>
          </div>
        </SectionReveal>
      </section>

      {/* LICENSE STRIP */}
      <section data-testid="license-strip" className="bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">Ugandan License</div>
          <blockquote className="mt-8 border-l-2 border-copper pl-8 md:pl-12 max-w-4xl">
            <p className="font-display font-light italic text-2xl md:text-3xl lg:text-[34px] leading-snug tracking-editorial">
              Maisha Pharma holds a Ugandan cannabis license covering cultivation, processing, domestic distribution, and export. Our commercial strategy is export-led, with domestic pathways pursued only where permitted under applicable Ugandan health, pharmaceutical, cosmetics, or consumer product regulations.
            </p>
          </blockquote>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section data-testid="who-we-are" className="bg-sand/50 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionReveal>
                <div className="overline">Who We Are</div>
                <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-editorial">A pharmaceutical platform — purpose-built for regulated markets.</h2>
                <p className="mt-6 text-ink2 max-w-md leading-relaxed">We operate at the intersection of Uganda's agricultural and energy advantages and pharmaceutical-grade operating discipline.</p>
                <p className="mt-4 text-ink2 max-w-md leading-relaxed text-sm">Member of the Uganda Medicinal Cannabis Consortium (UMCC).</p>
              </SectionReveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-12">
              {PILLARS.map((p, i) => (
                <SectionReveal key={p.label} delay={i * 90} className="h-full">
                  <div data-testid={`pillar-card-${i}`} className="border-t border-ink/15 pt-6 h-full">
                    <div className="flex items-start justify-between">
                      <p.icon size={20} strokeWidth={1.5} className="text-copper" />
                      <span className="font-mono text-[10px] tracking-[0.22em] text-ink2/70">0{i + 1}</span>
                    </div>
                    <h3 className="mt-7 font-display text-xl tracking-tight">{p.label}</h3>
                    <p className="mt-3 text-sm text-ink2 leading-relaxed">{p.body}</p>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPPORTUNITY */}
      <section data-testid="opportunity-section" className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionReveal>
                <div className="overline">The Opportunity</div>
                <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">Regulated demand is outpacing compliant supply.</h2>
                <p className="mt-6 text-ink2 max-w-md leading-relaxed">Europe's medicinal cannabis channel continues to expand, while pharmaceutical-grade supply remains constrained by certification, documentation, batch consistency, and import compliance requirements. This creates a structural opening for disciplined producers able to combine cost advantage with credible quality systems.</p>
              </SectionReveal>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-5">
              {OPP.map((s, i) => (
                <SectionReveal key={s.label} delay={i * 110} className="h-full">
                  <div className="bg-sand/60 p-8 md:p-10 h-full" data-testid={`opp-${i}`}>
                    <div className="flex items-start justify-between">
                      <s.icon size={20} strokeWidth={1.5} className="text-copper" />
                      <span className="font-mono text-[10px] tracking-[0.22em] text-ink2/70">0{i + 1}</span>
                    </div>
                    <h3 className="mt-7 font-display text-xl tracking-tight">{s.label}</h3>
                    <p className="mt-3 text-sm text-ink2 leading-relaxed">{s.body}</p>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE ARE BUILDING */}
      <section data-testid="what-we-build" className="relative bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionReveal>
              <div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">What We Are Building</div>
              <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-2xl text-bone">A vertically integrated, export-grade pharmaceutical platform.</h2>
            </SectionReveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
            {INFRA.map((it, i) => (
              <SectionReveal key={it.label} delay={i * 80} className="bg-forest h-full">
                <div data-testid={`infra-card-${i}`} className="group relative bg-bone/[0.03] p-7 md:p-9 h-full transition-colors duration-500 hover:bg-bone/[0.07]">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 border border-bone/20 flex items-center justify-center text-copper group-hover:bg-copper group-hover:text-forest group-hover:border-copper transition-colors">
                      <it.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.18em] text-bone/40">/0{i + 1}</span>
                  </div>
                  <h3 className="mt-8 font-display text-xl tracking-tight text-bone">{it.label}</h3>
                  <p className="mt-3 text-sm text-bone/60 leading-relaxed">{it.body}</p>
                  <div className="mt-8 h-px w-10 bg-copper transition-all duration-500 group-hover:w-20" />
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CINEMATIC UGANDA BAND */}
      <section data-testid="ambient-uganda" className="relative bg-bone">
        <div className="relative h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="/Images/uganda-crater-lake.jpg"
            alt="East African plateau · Uganda"
            className="absolute inset-0 w-full h-full object-cover scale-[1.02] transition-transform duration-[1800ms] ease-out hover:scale-[1.06]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-forest/35 via-forest/15 to-forest/55" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(23,38,33,0.35)_100%)]" />
          <div className="absolute inset-0 max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-12 md:pb-16">
            <div className="text-bone/85 text-[10px] tracking-[0.22em] uppercase">FIG.03 · Origin · East African Plateau</div>
            <h2 className="mt-4 font-display font-light text-3xl md:text-4xl lg:text-5xl text-bone leading-[1.05] tracking-editorial max-w-3xl">
              Built where the conditions begin.
            </h2>
            <div className="mt-6 flex flex-wrap gap-x-10 gap-y-2 text-[11px] tracking-[0.22em] uppercase text-bone/75">
              <span>Equatorial climate</span>
              <span className="text-copper">/</span>
              <span>Competitive power</span>
              <span className="text-copper">/</span>
              <span>Skilled agricultural capability</span>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section data-testid="approach-section" className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-0 md:px-12 py-0 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-16 items-stretch">
            <SectionReveal className="lg:col-span-6">
              <div className="relative h-[420px] md:h-[560px] overflow-hidden">
                <img src={IMG.indoor} alt="Controlled-environment indoor cultivation facility" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-[1.03]" loading="lazy" />
                <div className="absolute inset-0 bg-forest/15" />
                <div className="absolute bottom-6 left-6 text-bone/90 text-[11px] tracking-[0.22em] uppercase">FIG.02 · Controlled-Environment Indoor Cultivation</div>
              </div>
            </SectionReveal>
            <div className="lg:col-span-6 px-6 md:px-0 py-16 lg:py-0 flex flex-col justify-center">
              <SectionReveal>
                <div className="overline">Our Approach</div>
                <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">Compliance is not a feature. It is the foundation.</h2>
              </SectionReveal>
              <SectionReveal delay={140}>
                <p className="mt-6 text-ink2 max-w-lg leading-relaxed">Every operational layer — from cultivar selection to outbound logistics — is designed around the expectations of regulators, licensed importers, pharmaceutical partners, and patients.</p>
              </SectionReveal>
              <SectionReveal delay={260}>
                <ul className="mt-10 space-y-5">
                  {[["Compliance-first","Regulatory architecture precedes commercial design."],["Pharmaceutical quality","Operating standards designed around GACP and GMP-aligned workflows."],["Long-term partnerships","Built for institutional importers, licensed channels, and strategic technical partners."],["Scalable infrastructure","Modular cultivation and post-harvest capacity, with future extraction and manufacturing pathways."]].map(([k,v])=>(
                    <li key={k} className="grid grid-cols-12 gap-4 pb-5 border-b border-line/70">
                      <div className="col-span-4 md:col-span-3 font-display text-base md:text-lg text-forest">{k}</div>
                      <div className="col-span-8 md:col-span-9 text-sm text-ink2 leading-relaxed">{v}</div>
                    </li>
                  ))}
                </ul>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>

      {/* GLOBAL MARKET */}
      <section data-testid="global-market" className="relative bg-sand/60 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <SectionReveal>
                <div className="overline">Global Market Focus</div>
                <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">Calibrated for regulated international markets.</h2>
                <p className="mt-6 text-ink2 leading-relaxed max-w-md">Our export programme is structured around established and emerging medicinal cannabis import jurisdictions where compliance, quality systems, documentation, and reliable supply are decisive.</p>
                <div className="mt-10 grid grid-cols-2 gap-3 max-w-md">
                  {MARKETS.map((c) => (
                    <div key={c} className="bg-bone px-3 py-3 text-[11px] tracking-[0.18em] uppercase text-ink">{c}</div>
                  ))}
                </div>
              </SectionReveal>
            </div>
            <SectionReveal className="lg:col-span-8" delay={120}>
              <WorldMap />
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* INVESTORS & PARTNERS */}
      <section data-testid="investors-section" className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionReveal>
              <div className="overline">Investors & Partners</div>
              <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-2xl">Long-horizon capital. Institutional partnerships.</h2>
            </SectionReveal>
            <SectionReveal delay={120}>
              <Link to="/investors" data-testid="investors-section-cta" className="inline-flex items-center gap-2 text-sm border-b border-forest pb-1 text-forest hover:text-forest/75 transition-colors">Strategic positioning <ArrowUpRight size={14} /></Link>
            </SectionReveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { label: "Series A · $9.5M Phase I Raise", body: "We are currently executing a $9.5M Phase I Series A capital raise for the construction of our 1,250 m² GMP Clean Core and initial flowering canopy. This is an asset-backed deployment into a shovel-ready, licensed pharmaceutical infrastructure project." },
              { label: "Pharmaceutical Importers", body: "Establishing early forward-purchase and capacity allocations for our Phase I production cycles. Our facility is engineered to provide the supply chain stability and batch homogeneity required for the German, UK, and Australian markets." },
              { label: "Technical Partners", body: "Engineering, agronomy, GMP, analytical-method, and logistics collaborators across the value chain." },
            ].map((c, i) => (
              <SectionReveal key={c.label} delay={i * 100} className="h-full">
                <div className="bg-sand/50 p-8 md:p-10 h-full" data-testid={`investor-card-${i}`}>
                  <div className="font-mono text-[11px] tracking-[0.2em] text-copper uppercase">P/0{i + 1}</div>
                  <h3 className="mt-6 font-display text-2xl tracking-tight">{c.label}</h3>
                  <p className="mt-4 text-sm text-ink2 leading-relaxed">{c.body}</p>
                  <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm text-ink hover:text-forest transition-colors">Open inquiry <ArrowUpRight size={14} /></Link>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS */}
      <section data-testid="memberships" className="bg-sand/40 border-t border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-3 overline">Member of</div>
          <div className="md:col-span-9 grid grid-cols-1 sm:grid-cols-3 gap-10">
            <a href="https://www.thecannabischamber.com/" target="_blank" rel="noreferrer" className="group">
              <img src="/Images/Cannabis chamber of commerce logo.png" alt="The Cannabis Chamber" className="h-12 w-auto object-contain" loading="lazy" />
              <span className="mt-4 block text-sm text-ink group-hover:text-forest border-b border-line/60 pb-1 transition-colors w-fit">The Cannabis Chamber</span>
            </a>
            <a href="https://www.gcnc.global/" target="_blank" rel="noreferrer" className="group">
              <img src="/Images/GCNC logo.png" alt="Global Cannabinoid Network &amp; Council (GCNC)" className="h-12 w-auto object-contain" loading="lazy" />
              <span className="mt-4 block text-sm text-ink group-hover:text-forest border-b border-line/60 pb-1 transition-colors w-fit">Global Cannabinoid Network &amp; Council (GCNC)</span>
            </a>
            <div>
              <img src="/Images/UMCC Logo.png" alt="Uganda Medicinal Cannabis Consortium (UMCC)" className="h-12 w-auto object-contain" loading="lazy" />
              <span className="mt-4 block text-sm text-ink border-b border-line/60 pb-1 w-fit">Uganda Medicinal Cannabis Consortium (UMCC) · Member</span>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT PROMPT */}
      <section data-testid="contact-prompt" className="bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <SectionReveal className="lg:col-span-8">
            <div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">Direct Inquiry</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-[52px] leading-[1.05] tracking-editorial max-w-3xl text-bone">Speak directly with our founding team about Maisha Pharma's regulated export platform.</h2>
          </SectionReveal>
          <SectionReveal className="lg:col-span-4 lg:text-right" delay={120}>
            <Link to="/contact" data-testid="home-cta-contact" className="inline-flex items-center gap-2 bg-bone text-ink px-7 py-4 text-sm tracking-wide hover:bg-sand transition-colors">Open inquiry form <ArrowUpRight size={16} /></Link>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
